/**
 * Author: Andrey Morozov
 * Confidential. Proprietary and confidential. Unauthorized copying or distribution is prohibited.
 */

/**
 * GatewayClient — the IAM.Core Web Console's typed client for the
 * iam-core-gateway HTTP surface.
 *
 * It is the single place the console talks to the gateway. Every request
 * carries the caller's tenant / principal / scopes / groups as the headers the
 * gateway's `httpapi.callContext` reads (X-Tenant-ID, X-IAM-Principal,
 * X-IAM-Scopes, X-IAM-Groups). The client is transport-only: it knows the
 * endpoints and the wire shapes, not UI state.
 */

import type {
  ApprovalDecision,
  GatewayApproval,
  GatewayIdentity,
  RunSessionRequest,
  SessionView,
  ToolDescriptor,
} from "./types";

/** A typed error carrying the HTTP status and the gateway's error message. */
export class GatewayError extends Error {
  readonly status: number;
  readonly endpoint: string;
  /** JSON-RPC error code, when the failure came from the /mcp endpoint. */
  readonly rpcCode?: number;

  constructor(message: string, opts: { status: number; endpoint: string; rpcCode?: number }) {
    super(message);
    this.name = "GatewayError";
    this.status = opts.status;
    this.endpoint = opts.endpoint;
    this.rpcCode = opts.rpcCode;
  }
}

export type GatewayClientOptions = {
  /** Base URL of the gateway. Empty string means same-origin (dev proxy). */
  baseUrl: string;
  identity: GatewayIdentity;
  /** Use the same-origin opaque HttpOnly BFF cookie and omit spoofable identity headers. */
  cookieAuth?: boolean;
  /** Injectable fetch — defaults to global fetch; tests pass a mock. */
  fetchImpl?: typeof fetch;
};

type JsonRpcResponse = {
  jsonrpc: string;
  id: unknown;
  result?: unknown;
  error?: { code: number; message: string };
};

let rpcCounter = 0;

export class GatewayClient {
  private readonly baseUrl: string;
  private readonly identity: GatewayIdentity;
  private readonly fetchImpl: typeof fetch;
  private readonly cookieAuth: boolean;

  constructor(opts: GatewayClientOptions) {
    this.baseUrl = opts.baseUrl.replace(/\/$/, "");
    this.identity = opts.identity;
    this.fetchImpl = opts.fetchImpl ?? globalThis.fetch.bind(globalThis);
    this.cookieAuth = opts.cookieAuth ?? false;
  }

  /** Returns the identity headers attached to every gateway request. */
  private headers(): Headers {
    const h = new Headers({ "Content-Type": "application/json" });
    if (!this.cookieAuth) {
      h.set("X-Tenant-ID", this.identity.tenantId);
      if (this.identity.principal) h.set("X-IAM-Principal", this.identity.principal);
      if (this.identity.scopes.length > 0) {
        h.set("X-IAM-Scopes", this.identity.scopes.join(" "));
      }
      if (this.identity.groups && this.identity.groups.length > 0) {
        h.set("X-IAM-Groups", this.identity.groups.join(" "));
      }
    }
    return h;
  }

  private async request<T>(path: string, init: RequestInit, expectBody = true): Promise<T> {
    const url = `${this.baseUrl}${path}`;
    const res = await this.fetchImpl(url, { ...init, credentials: "same-origin", headers: this.headers() });
    if (!res.ok) {
      let message = `gateway ${res.status}`;
      try {
        /** Body. */
        const body = (await res.json()) as { error?: string };
        if (body.error) message = body.error;
      } catch {
        /* non-JSON error body */
      }
      throw new GatewayError(message, { status: res.status, endpoint: path });
    }
    if (!expectBody) return undefined as T;
    return (await res.json()) as T;
  }

  /** GET /healthz — gateway liveness probe. */
  async health(): Promise<{ status: string; version: string; uptime_sec: number }> {
    return this.request("/healthz", { method: "GET" });
  }

  /** GET /v1/tools — the tenant's tool catalog. */
  async listTools(): Promise<ToolDescriptor[]> {
    const body = await this.request<{ tools: ToolDescriptor[] }>("/v1/tools", {
      method: "GET",
    });
    return body.tools ?? [];
  }

  /** POST /v1/feature-flags — toggle a tenant feature flag. */
  async setFeatureFlag(flag: string, enabled: boolean): Promise<void> {
    await this.request(
      "/v1/feature-flags",
      { method: "POST", body: JSON.stringify({ flag, enabled }) },
      false,
    );
  }

  /** POST /v1/agent/sessions — create and run an agent session synchronously. */
  async runSession(req: RunSessionRequest): Promise<SessionView> {
    return this.request<SessionView>("/v1/agent/sessions", {
      method: "POST",
      body: JSON.stringify(req),
    });
  }

  /** GET /v1/agent/sessions/{id} — fetch a session and its audit trail. */
  async getSession(id: string): Promise<SessionView> {
    return this.request<SessionView>(`/v1/agent/sessions/${encodeURIComponent(id)}`, {
      method: "GET",
    });
  }

  /** POST /v1/agent/sessions/{id}/cancel — request cancellation. */
  async cancelSession(id: string): Promise<void> {
    await this.request(
      `/v1/agent/sessions/${encodeURIComponent(id)}/cancel`,
      { method: "POST" },
      false,
    );
  }

  /** POST .../approvals/{approvalID} — post an approval decision. */
  async decideApproval(
    sessionId: string,
    approvalId: string,
    decision: { decision: ApprovalDecision; decided_by: string; comment?: string },
  ): Promise<void> {
    await this.request(
      `/v1/agent/sessions/${encodeURIComponent(sessionId)}/approvals/${encodeURIComponent(
        approvalId,
      )}`,
      { method: "POST", body: JSON.stringify(decision) },
      false,
    );
  }

  /**
   * POST /mcp — invoke a tool directly over the MCP JSON-RPC endpoint. Used by
   * the console to call e.g. docs.hybrid_search outside an agent session.
   * A JSON-RPC error is surfaced as a GatewayError carrying the rpc code.
   */
  async callTool(name: string, args: Record<string, unknown>): Promise<unknown> {
    const id = ++rpcCounter;
    const res = await this.fetchImpl(`${this.baseUrl}/mcp`, {
      method: "POST",
      headers: this.headers(),
      body: JSON.stringify({
        jsonrpc: "2.0",
        id,
        method: "tools/call",
        params: { name, arguments: args },
      }),
    });
    if (!res.ok) {
      throw new GatewayError(`gateway ${res.status}`, {
        status: res.status,
        endpoint: "/mcp",
      });
    }
    /** Body. */
    const body = (await res.json()) as JsonRpcResponse;
    if (body.error) {
      throw new GatewayError(body.error.message, {
        status: res.status,
        endpoint: "/mcp",
        rpcCode: body.error.code,
      });
    }
    return body.result;
  }
}

export type { GatewayApproval };
